import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const heroImages = [
  '/media/sun_city_hero1.webp',
  '/media/sun_city_hero2.webp',
  '/media/sun_city_hero3.webp',
  '/media/sun_city_hero4.webp',
  '/media/sun_city_hero5.webp',
  '/media/sun_city_hero6.webp',
];

export default function Hero({ onEnquireClick }) {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-advance slider
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const goToPrev = () => {
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  };

  const goToNext = () => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-16 md:pt-18 lg:pt-20"
    >
      {/* Full Width Background Image Slider */}
      <div className="relative w-full h-[85vh] sm:h-[80vh] md:h-[85vh] lg:h-[90vh] overflow-hidden">
        <div className="absolute inset-0 flex">
          <div
            className="flex w-full h-full transition-transform duration-700 ease-in-out will-change-transform"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {heroImages.map((img, index) => (
              <img
                key={index}
                src={img}
                alt="Sai Sun City"
                // Apply high priority ONLY to the first slide
                fetchPriority={index === 0 ? "high" : "low"}
                // Lazy load every slide AFTER the first
                loading={index === 0 ? "eager" : "lazy"}
                decoding={index === 0 ? "sync" : "async"}
                className="w-full h-full object-cover flex-shrink-0"
              />
            ))}
          </div>
        </div>

        {/* Left Info Panel - Overlaying the image */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="absolute left-3 sm:left-4 md:left-6 lg:left-8 top-4 sm:top-6 md:top-8 z-10 w-[calc(100%-24px)] sm:w-auto sm:max-w-[280px] md:max-w-[320px]"
        >
          <div className="bg-white/95 backdrop-blur-sm p-4 sm:p-5 md:p-6 shadow-xl">
            {/* Possession Badge */}
            <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">
              Possession : <span className="font-semibold text-gray-800">Dec 2026</span>
            </p>

            {/* Title */}
            <h1 className="font-serif text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mb-0.5">
              SAI SUN CITY
            </h1>
            <p className="text-gray-600 text-xs sm:text-sm mb-0.5">by Paradise Group</p>
            <p className="text-gray-500 text-xs sm:text-sm mb-4">at Upper Kharghar</p>

            {/* Offers Box */}
            <div className="border-2 border-dashed border-[#9A7B4F]/50 p-3 mb-4 bg-[#9A7B4F]/5">
              <div className="space-y-1.5">
                <p className="text-xs sm:text-sm text-[#7A5F3A] flex items-center gap-2">
                  <span className="text-[#9A7B4F]">◆</span>
                  Spot Booking Offers Available*
                </p>
                <p className="text-xs sm:text-sm text-[#7A5F3A] flex items-center gap-2">
                  <span className="text-[#9A7B4F]">◆</span>
                  Flexible CLP Payment Schedule*
                </p>
              </div>
            </div>

            {/* Price */}
            <div className="mb-4">
              <p className="text-xs sm:text-sm text-gray-600 mb-1">1 & 2 BHK Homes Starts at</p>
              <p className="text-2xl sm:text-3xl font-bold text-gray-900">
                <span className="text-[#9A7B4F]">₹</span> 43 Lacs<sup className="text-sm">*</sup>
                <span className="text-sm sm:text-base font-normal text-gray-500"> ++ Taxes</span>
              </p>
            </div>

            {/* CTA Button */}
            <Button
              onClick={onEnquireClick}
              className="bg-[#9A7B4F] hover:bg-[#8A6B3F] text-white rounded-sm px-6 py-5 sm:py-6 text-sm font-semibold w-full sm:w-auto"
              data-testid="hero-enquire-btn"
            >
              Enquire Now
            </Button>
          </div>
        </motion.div>

        {/* Slider Navigation - Desktop Only */}
        <button
          onClick={goToPrev}
          className="hidden sm:flex absolute left-2 sm:left-4 bottom-1/2 translate-y-1/2 z-10 p-2 bg-white/80 hover:bg-white transition-all shadow-lg items-center justify-center"
          data-testid="hero-prev-btn"
        >
          <ChevronLeft className="w-5 h-5 text-gray-700" />
        </button>
        <button
          onClick={goToNext}
          className="hidden sm:flex absolute right-2 sm:right-4 lg:right-[340px] bottom-1/2 translate-y-1/2 z-10 p-2 bg-white/80 hover:bg-white transition-all shadow-lg items-center justify-center"
          data-testid="hero-next-btn"
        >
          <ChevronRight className="w-5 h-5 text-gray-700" />
        </button>

        {/* Slider Dots */}
        <div className="absolute bottom-4 sm:bottom-6 left-1/2 lg:left-[calc(50%-160px)] -translate-x-1/2 z-10 flex gap-2">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all ${
                index === currentSlide 
                  ? 'bg-[#9A7B4F] scale-110' 
                  : 'bg-white/70 hover:bg-white'
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
